import captureError from './captureError'
import { useEffect, useRef, useState } from 'react'
const cachedScripts = {}
export default function useScript(
  src: string,
  attributes?: Record<string, string> | null | undefined,
  blockLoading?: boolean,
  suppressErrorLogging?: boolean,
): [boolean, boolean, () => void] {
  // Keeping track of script loaded and error state
  const [state, setState] = useState({
    loaded: false,
    error: false,
  })
  const [reload, setReload] = useState(false)
  const scriptRef = useRef(null)

  const loadScript = () => {
    setReload(false)

    if (blockLoading) {
      // do nothing
    }
    // If cachedScripts array already includes src that means another instance ...
    // ... of this hook already loaded this script, so no need to load again.
    else if (cachedScripts[src]) {
      cachedScripts[src].then(
        () => {
          setState({
            loaded: true,
            error: false,
          })
        },
        () => {
          setState({
            loaded: true,
            error: true,
          })
        },
      )
    } else {
      const promiseWithResolvers = {
        // this is needed only for the edge case where 2 loaders wait for the same script concurrently
        resolve: undefined,
        reject: undefined,
        promise: undefined,
      }
      promiseWithResolvers.promise = new Promise((resolve, reject) => {
        promiseWithResolvers.resolve = resolve
        promiseWithResolvers.reject = reject
      })
      promiseWithResolvers.promise.catch(() => {}) // no-op to suppress UnhandledPromiseRejection for 99% case where there's 1 loader
      cachedScripts[src] = promiseWithResolvers.promise
      // Create script
      let script = document.createElement('script')
      script.src = src
      script.async = true
      scriptRef.current = script

      if (attributes) {
        Object.keys(attributes || {}).forEach(key => {
          if (attributes) script.setAttribute(key, attributes[key])
        })
      }

      // Script event listener callbacks for load and error
      const onScriptLoad = () => {
        setState({
          loaded: true,
          error: false,
        })
        promiseWithResolvers.resolve()
      }

      const onScriptError = () => {
        // Remove from cachedScripts we can try loading again
        delete cachedScripts[src]
        script.remove()
        promiseWithResolvers.reject()
        setState({
          loaded: true,
          error: true,
        })
        if (suppressErrorLogging) return
        captureError(new Error(`${src} didn't load`))
      }

      // Do not remove these event listeners on cleanup.
      // They are required when useScript is used again before a script has loaded.
      // When the effect cleanup happens while a script is loading, the onScriptLoad function is never called.
      // The cached script logic prevents from adding multiple event listeners.
      // When calling retry, the removeCachedScript will remove the node (and attached listeners).
      script.addEventListener('load', onScriptLoad)
      script.addEventListener('error', onScriptError)

      // Add script to document body
      if (document.body) document.body.appendChild(script)
    }
  }

  useEffect(
    loadScript,
    [src, blockLoading], // Only re-run effect if script src changes
  )
  useEffect(() => {
    if (reload) {
      loadScript()
    }
  }, [state])

  const removeCachedScript = () => {
    if (scriptRef.current) {
      scriptRef.current.remove()
    }
    delete cachedScripts[src]
  }

  const retry = () => {
    removeCachedScript()
    setState({
      loaded: false,
      error: false,
    })
    setReload(true)
  }

  return [state.loaded, state.error, retry]
}
